import { useState, useEffect, useMemo } from 'react';

import { Box, Link, Avatar } from '@mui/material';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Link as RouterLink, useParams } from 'react-router-dom';
import useSWR from 'swr';

import { financeConfigurationAtom } from '~atoms';
import { RevenuesService } from '~features/finances';
import { useDebounce } from '~hooks';
import { getInitials } from '~utils/stringUtils';

import Header from './Header';
import { useStyles } from './style';
import { Table, Label, SearchBar, Page } from '../../../../components';
import {
	isObject,
	isEmptyString,
	isEmptyArray,
	decimalAmount,
	getFinanceTableHeaders,
} from '../../../../shared/utility';
import * as actions from '../../../../store/actions';

interface OrganisationsProps {
	date?: string;
	onFetchBusinessRevenue?(...args: unknown[]): unknown;
	businessRevenue?: {
		data?: object;
		loading?: boolean;
		error?: object | string;
	};
}

const service = new RevenuesService();

const Organisations = (props: OrganisationsProps) => {
	const { t } = useTranslation();
	const financeConfiguration = useAtomValue(financeConfigurationAtom);

	const classes = useStyles();
	const { date, title } = useParams();
	const [searchValue, setSearchValue] = useState('');
	const [shouldSearch, setShouldSearch] = useState(false);
	const [isShowingSearchResults, setIsShowingSearchResults] = useState(false);

	const shouldSearchOnDebounce = false;

	const fetchParameters = useMemo(() => {
		if (shouldSearch) {
			setShouldSearch(false);
		}

		return {
			searchTerm: searchValue || undefined,
			excludeOneOffPayments: financeConfiguration.excludeOneOffPayments,
		};
	}, [financeConfiguration.excludeOneOffPayments, shouldSearch]);
	const { data: businessRevenueData, isLoading: businessRevenueLoading } = useSWR(
		[service.basePath, date, service.businessSubPath, fetchParameters],
		([_1, dateSubPath, _2, args]) => service.getBusinessRevenuesUnmapped(dateSubPath, args)
	);

	const handleSearchClick = () => {
		if (!isEmptyString(searchValue)) {
			setShouldSearch(true);
			setIsShowingSearchResults(true);
		}
		if (isEmptyString(searchValue) && isShowingSearchResults) {
			setShouldSearch(true);
			setIsShowingSearchResults(false);
		}
	};

	const handleResetSearch = () => {
		setSearchValue('');
		if (isShowingSearchResults === true) {
			setShouldSearch(true);
		}
		setIsShowingSearchResults(false);
	};

	const handleKeyUp = (e) => {
		switch (e.key) {
			case 'Enter':
				handleSearchClick();
				break;
			default:
				return;
		}
	};

	const events = {
		onClick: handleSearchClick,
		onChange: (e) => setSearchValue(e.target.value),
		onClear: handleResetSearch,
		onKeyUp: (e) => handleKeyUp(e),
	};

	const debouncedSearchValue = useDebounce(searchValue, 500);

	useEffect(() => {
		if (shouldSearchOnDebounce) {
			handleSearchClick();
		}
	}, [shouldSearchOnDebounce, debouncedSearchValue]);

	const headers = getFinanceTableHeaders(true);

	const tableHeader = [
		{ name: 'organisations', content: t('ui.category.organisations') },
		...headers,
	];

	const statusColor = {
		open: 'default',
		paid: 'success',
	};

	const statusText = {
		open: t('ui.open'),
		paid: t('ui.label.paid'),
	};

	const emptyBody =
		(
			isObject(businessRevenueData) &&
			!businessRevenueLoading &&
			isEmptyArray(businessRevenueData.billings)
		) ?
			Array(1)
				.fill(Array(tableHeader.length).fill())
				.map((arr) => arr.map(() => ({ content: '-' })))
		:	null;

	const loadingBody =
		!isObject(businessRevenueData) || businessRevenueLoading ?
			Array(4)
				.fill(Array(tableHeader.length).fill())
				.map((arr) => arr.map(() => ({ loading: true })))
		:	null;

	const dataBody =
		isObject(businessRevenueData) && !businessRevenueLoading ?
			businessRevenueData.billings.map((revenue) => [
				{
					content: (
						<Box alignItems='center' display='flex' height='19px' width='250px'>
							{revenue.organisationReference.logo ?
								<Avatar
									className={classes.avatar}
									src={revenue.organisationReference.logo}
									variant='square'
								/>
							:	<Avatar variant='square'>{getInitials(revenue.organisationReference.name)}</Avatar>}
							<Box pl={1}>
								<Link
									color='inherit'
									component={RouterLink}
									to={`/financeManagement/revenue/${date}/${title}/partner/${revenue.organisationReference.id}/${encodeURIComponent(revenue.organisationReference.name)}`}
								>
									{`${revenue.organisationReference.name}`}
								</Link>
							</Box>
						</Box>
					),
				},
				{ content: `${decimalAmount(revenue.amount.subtotal, revenue.amount)}` },
				{ content: `${decimalAmount(revenue.amount.discount, revenue.amount)}` },
				{
					content: `${decimalAmount(revenue.amount.subtotal - revenue.amount.discount, revenue.amount)}`,
				},
				{ content: `${decimalAmount(revenue.amount.priceVat, revenue.amount)}` },
				{ content: `${decimalAmount(revenue.amount.refund, revenue.amount)}` },
				{ content: `${decimalAmount(revenue.amount.totalPrice, revenue.amount)}` },
				{ content: `${decimalAmount(revenue.open, revenue.amount)}` },
				{
					content: <Label type={statusColor[revenue.status]}>{statusText[revenue.status]}</Label>,
				},
			])
		:	Array(3)
				.fill(Array(tableHeader.length).fill())
				.map((arr) => arr.map(() => ({ loading: true })));

	const titleWithInformation =
		isObject(businessRevenueData) ?
			{
				header: t('views.financeHistory.organisationalBilling.header'),
			}
		:	null;

	return (
		<Page className={classes.root} title={t('ui.details')}>
			<Header title={title} />
			<Box pt={3}>
				<SearchBar
					hasSearchButton
					placeholder={t('views.usersAndOrganisations.organisations.searchPlaceholder')}
					searchEvents={events}
					searchValue={searchValue}
				/>
			</Box>
			<Table
				body={loadingBody || emptyBody || dataBody}
				header={tableHeader}
				isNotPaginate={true}
				loading={businessRevenueLoading}
				noTitle={true}
				titleWithInformation={titleWithInformation}
				withoutPages={true}
			/>
		</Page>
	);
};

const mapStateToProps = (state) => {
	return {
		businessRevenue: state.details.businessRevenue,
	};
};

const mapDispatchToProps = (dispatch) => {
	return {
		onFetchBusinessRevenue: (date, filters) =>
			dispatch(actions.fetchBusinessRevenue(date, filters)),
	};
};

export default connect(mapStateToProps, mapDispatchToProps)(Organisations);
